import { useEffect, useState } from 'react';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { useNavigate } from 'react-router-dom';
import { LandingPage } from '@leagueplatform/solutions-foundation';
import { useIntl } from '@leagueplatform/locales';
import { QueryParamModal } from 'components/modals/query-param-modal.component';
import { AUTH0_ERROR_TYPES } from 'common/constants';

import { Auth0Error } from 'types/auth.types';
import { ErrorState } from 'components/error-state/error-state.component';
import { ERROR_TYPES } from 'hooks/use-error-content/use-error-content.hook';
import ErrorIcon from 'assets/error-icon.svg';
import SubtleErrorIcon from 'assets/error-subtle-icon.svg';
import { PatientPortalUIConfigOptions } from 'utils/patient-portal-ui-config';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { PATIENT_PORTAL_SYSTEM_IDS } from 'types/patient-portal-system.types';
import { useUrlParam } from 'hooks/use-url-param/use-url-param.hook';
import { SignInFooter } from './sign-in-footer.component';

export const PreLoginLandingPage = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [authError, setAuthError] = useState<Auth0Error | null>(null);
  const { systemId } = usePatientPortalContext();

  const { urlParam: ineligibleParam, removeParam: removeIneligibleParam } =
    useUrlParam('user_ineligible');

  const [isIneligible, setIsIneligible] = useState(
    () => ineligibleParam !== null,
  );

  useEffect(() => {
    if (ineligibleParam !== null) {
      removeIneligibleParam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isIneligible) {
    return (
      <ErrorState
        error={null}
        errorType={ERROR_TYPES.INELIGIBLE_ERROR}
        isFullPage
        imageSrc={SubtleErrorIcon}
        buttonText={formatMessage({ id: 'STR_CLOSE' })}
        buttonPriority="secondary"
        buttonAction={() => setIsIneligible(false)}
      />
    );
  }

  if (authError) {
    switch (authError.type) {
      case AUTH0_ERROR_TYPES.LOGIN_REQUIRED:
      default:
        return (
          <ErrorState
            isFullPage
            error={authError}
            errorType={ERROR_TYPES.AUTH_ERROR}
            buttonAction={() => {
              navigate(APP_MANAGED_ROUTES.LANDING_PAGE, { replace: true });
              setAuthError(null);
            }}
            imageSrc={ErrorIcon}
          />
        );
    }
  }
  const { logo, landing } = PatientPortalUIConfigOptions[systemId];

  return (
    <>
      <QueryParamModal isPublic />
      <LandingPage
        logo={{
          src: logo,
          alt: formatMessage({ id: 'STR_APP_NAME' }),
          css: { marginBlockEnd: '$one', width: '240px' },
        }}
        backgroundImage={{ src: landing.backgroundImageSrc }}
        primaryButton={{
          buttonText: formatMessage({ id: 'STR_SIGN_IN' }),
          onClick: () => navigate(APP_MANAGED_ROUTES.SIGN_IN),
          css: {
            width: '327px',
            maxWidth: 'unset',
          },
        }}
        secondaryButtons={
          systemId === PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH
            ? [
                {
                  buttonText: formatMessage({ id: 'STR_SIGN_UP' }),
                  onClick: () => navigate(APP_MANAGED_ROUTES.SIGN_UP),
                  css: {
                    width: '327px',
                    maxWidth: 'unset',
                  },
                },
              ]
            : undefined
        }
        onAuthenticated={(appState) =>
          navigate(appState?.redirectPath || APP_MANAGED_ROUTES.HOME)
        }
        onAuthError={(error) => {
          setAuthError(error);
        }}
        signInFooter={<SignInFooter />}
        ellipsis
      />
    </>
  );
};
